// YES, I know, the ascii art is a bit over the top, but it's fun and I like it!
//
// Your work is going to fill a large part of your life,
// and the only way to be truly satisfied is to do what you believe is great work.
// And the only way to do great work is to love what you do.
// RIP Steve Jobs, you were a true visionary!
//
// ... and now 😄 ENJOY!
//
// ... and before you ask, the answer will always be 42!

import '@/assets/main.css'
import '@/assets/no-namespace/no-namespace.css'
// eslint-disable-next-line import/no-unresolved
import 'virtual:uno.css'

import type { App } from 'vue'
import { defineAsyncComponent } from 'vue'
import { createRouter, createWebHashHistory } from 'vue-router'

import { createApplication, createCustomElement, createMSPA, createPlugin } from '@/lib/mspa'
// *****************************************************************************************************************************************************
//  $$$$$$\
// $$  __$$\
// $$ /  \__| $$$$$$\  $$$$$$\$$$$\  $$$$$$\$$$$\   $$$$$$\  $$$$$$$\
// $$ |      $$  __$$\ $$  _$$  _$$\ $$  _$$  _$$\ $$  __$$\ $$  __$$\
// $$ |      $$ /  $$ |$$ / $$ / $$ |$$ / $$ / $$ |$$ /  $$ |$$ |  $$ |
// $$ |  $$\ $$ |  $$ |$$ | $$ | $$ |$$ | $$ | $$ |$$ |  $$ |$$ |  $$ |
// \$$$$$$  |\$$$$$$  |$$ | $$ | $$ |$$ | $$ | $$ |\$$$$$$  |$$ |  $$ |
//  \______/  \______/ \__| \__| \__|\__| \__| \__| \______/ \__|  \__|
// *****************************************************************************************************************************************************

const addAppContainerClasses = <T extends App>(app: T, ...tokens: string[]) => {
  const container: HTMLElement | undefined = app?._container?.host || app?._container
  container?.classList.add(...tokens)
}

const globalInstanceConfiguration = async ({ app }: { app: App }) => {
  addAppContainerClasses(app, import.meta.env.RMT_UI_CSS_NAMESPACE)
}

// *****************************************************************************************************************************************************
//
// $$$$$$$\  $$\                     $$\
// $$  __$$\ $$ |                    \__|
// $$ |  $$ |$$ |$$\   $$\  $$$$$$\  $$\ $$$$$$$\   $$$$$$$\
// $$$$$$$  |$$ |$$ |  $$ |$$  __$$\ $$ |$$  __$$\ $$  _____|
// $$  ____/ $$ |$$ |  $$ |$$ /  $$ |$$ |$$ |  $$ |\$$$$$$\
// $$ |      $$ |$$ |  $$ |$$ |  $$ |$$ |$$ |  $$ | \____$$\
// $$ |      $$ |\$$$$$$  |\$$$$$$$ |$$ |$$ |  $$ |$$$$$$$  |
// \__|      \__| \______/  \____$$ |\__|\__|  \__|\_______/
//                         $$\   $$ |
//                         \$$$$$$  |
//                          \______/
//
// *****************************************************************************************************************************************************

const plugins = {
  pinia: createPlugin({
    id: 'pinia',
    load: async () => {
      const [pinia, persistedState] = await Promise.all([
        import('pinia'),
        import('pinia-plugin-persistedstate')
      ])

      return pinia.createPinia().use(persistedState.createPersistedState())
    }
  }),
  vueQuery: createPlugin({
    id: 'vue-query',
    load: async () => {
      const { VueQueryPlugin } = await import('@tanstack/vue-query')
      return VueQueryPlugin
    }
  }),
  vueFinalModal: createPlugin({
    id: 'vue-final-modal',
    load: async () => {
      const vfm = await import('vue-final-modal')
      return vfm.createVfm()
    }
  }),
  formKit: createPlugin({
    id: 'form-kit',
    load: async () => {
      const [{ plugin }, { defaultConfig }] = await Promise.all([
        import('@formkit/vue'),
        import('@/config/formkit')
      ])

      return [plugin, defaultConfig]
    }
  })
}

// *****************************************************************************************************************************************************
//
// $$$$$$\                        $$\                                   $$$$$$$$\ $$\                                              $$\
// $$  __$$\                       $$ |                                  $$  _____|$$ |                                             $$ |
// $$ /  \__|$$\   $$\  $$$$$$$\ $$$$$$\    $$$$$$\  $$$$$$\$$$$\        $$ |      $$ | $$$$$$\  $$$$$$\$$$$\   $$$$$$\  $$$$$$$\ $$$$$$\    $$$$$$$\
// $$ |      $$ |  $$ |$$  _____|\_$$  _|  $$  __$$\ $$  _$$  _$$\       $$$$$\    $$ |$$  __$$\ $$  _$$  _$$\ $$  __$$\ $$  __$$\\_$$  _|  $$  _____|
// $$ |      $$ |  $$ |\$$$$$$\    $$ |    $$ /  $$ |$$ / $$ / $$ |      $$  __|   $$ |$$$$$$$$ |$$ / $$ / $$ |$$$$$$$$ |$$ |  $$ | $$ |    \$$$$$$\
// $$ |  $$\ $$ |  $$ | \____$$\   $$ |$$\ $$ |  $$ |$$ | $$ | $$ |      $$ |      $$ |$$   ____|$$ | $$ | $$ |$$   ____|$$ |  $$ | $$ |$$\  \____$$\
// \$$$$$$  |\$$$$$$  |$$$$$$$  |  \$$$$  |\$$$$$$  |$$ | $$ | $$ |      $$$$$$$$\ $$ |\$$$$$$$\ $$ | $$ | $$ |\$$$$$$$\ $$ |  $$ | \$$$$  |$$$$$$$  |
//  \______/  \______/ \_______/    \____/  \______/ \__| \__| \__|      \________|\__| \_______|\__| \__| \__| \_______|\__|  \__|  \____/ \_______/
//
// *****************************************************************************************************************************************************

const customElements = [
  createCustomElement({
    id: 'trailer-booking-entrypoint',
    component: async () =>
      defineAsyncComponent(() => import('@/elements/TrailerBookingEntrypoint.vue')),
    configureInstance: globalInstanceConfiguration,
    shadowRoot: false
  }),

  createCustomElement({
    id: 'trailer-data-sheet-modal',
    component: async () =>
      defineAsyncComponent(() => import('@/elements/TrailerDataSheetModal.vue')),
    configureInstance: globalInstanceConfiguration,
    shadowRoot: false
  }),

  createCustomElement({
    id: 'driving-license-calculator',
    component: async () =>
      defineAsyncComponent(() => import('@/elements/DrivingLicenseCalculator.vue')),
    configureInstance: globalInstanceConfiguration,
    shadowRoot: false
  }),

  createCustomElement({
    id: 'registration-form',
    component: async () => defineAsyncComponent(() => import('@/elements/RegistrationForm.vue')),
    configureInstance: globalInstanceConfiguration,
    plugins: [plugins.formKit],
    shadowRoot: false
  }),

  createCustomElement({
    id: 'one-way-form',
    component: async () => defineAsyncComponent(() => import('@/elements/OneWayForm.vue')),
    configureInstance: globalInstanceConfiguration,
    plugins: [plugins.formKit],
    shadowRoot: false
  })
]

// *****************************************************************************************************************************************************
//
//  $$$$$$\                      $$\ $$\                     $$\     $$\
// $$  __$$\                     $$ |\__|                    $$ |    \__|
// $$ /  $$ | $$$$$$\   $$$$$$\  $$ |$$\  $$$$$$$\ $$$$$$\ $$$$$$\   $$\  $$$$$$\  $$$$$$$\   $$$$$$$\
// $$$$$$$$ |$$  __$$\ $$  __$$\ $$ |$$ |$$  _____|\____$$\\_$$  _|  $$ |$$  __$$\ $$  __$$\ $$  _____|
// $$  __$$ |$$ /  $$ |$$ /  $$ |$$ |$$ |$$ /      $$$$$$$ | $$ |    $$ |$$ /  $$ |$$ |  $$ |\$$$$$$\
// $$ |  $$ |$$ |  $$ |$$ |  $$ |$$ |$$ |$$ |     $$  __$$ | $$ |$$\ $$ |$$ |  $$ |$$ |  $$ | \____$$\
// $$ |  $$ |$$$$$$$  |$$$$$$$  |$$ |$$ |\$$$$$$$\\$$$$$$$ | \$$$$  |$$ |\$$$$$$  |$$ |  $$ |$$$$$$$  |
// \__|  \__|$$  ____/ $$  ____/ \__|\__| \_______|\_______|  \____/ \__| \______/ \__|  \__|\_______/
//           $$ |      $$ |
//           $$ |      $$ |
//           \__|      \__|
//
// *****************************************************************************************************************************************************

const applications = [
  createApplication({
    container: [document.querySelector('#app-booking-select')],
    component: async () => defineAsyncComponent(() => import('@/layouts/DefaultLayout.vue')),
    configureApp: async ({ app }) => {
      app.use(
        createRouter({
          history: createWebHashHistory(),
          routes: [
            {
              name: 'booking-select',
              path: '/',
              component: () => import('@/apps/BookingMap/BookingMap.vue'),
              children: [
                {
                  name: 'booking-map-trailer-list',
                  path: '/',
                  component: () => import('@/apps/BookingMap/TrailerList/TrailerList.vue')
                },
                {
                  name: 'booking-map-trailer-show',
                  path: '/trailer/:id',
                  props: true,
                  component: () => import('@/apps/BookingMap/TrailerShow.vue')
                }
              ]
            }
          ]
        })
      )
    },
    configureInstance: globalInstanceConfiguration
  })
]

// *****************************************************************************************************************************************************
//
// $$$$$$$$\
// $$  _____|
// $$ |      $$\   $$\  $$$$$$\   $$$$$$$\
// $$$$$\    \$$\ $$  |$$  __$$\ $$  _____|
// $$  __|    \$$$$  / $$$$$$$$ |$$ /
// $$ |       $$  $$<  $$   ____|$$ |
// $$$$$$$$\ $$  /\$$\ \$$$$$$$\ \$$$$$$$\
// \________|\__/  \__| \_______| \_______|
//
// *****************************************************************************************************************************************************

// create the MSPA environment and run it
await createMSPA({
  customElements,
  applications,
  plugins: [plugins.pinia, plugins.vueQuery, plugins.vueFinalModal]
}).run()
